// M14 Playlists – Liste.
import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { can, qk, useMe, usePlaylists } from '../api/queries';
import type { Playlist, PlaylistDetail } from '../api/types';
import { usePendingMap } from '../api/approvals';
import { PendingPill } from '../components/Approval';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Dialog, Empty, Field, formatDuration, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';

export function PlaylistsPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: playlists, isLoading } = usePlaylists();
  const navigate = useNavigate();
  const [create, setCreate] = useState(false);
  const [q, setQ] = useState('');
  const details = useQueries({
    queries: (playlists ?? []).map((p) => ({
      queryKey: [...qk.playlist(p.id), me?.activeTenantId ?? 'none'],
      queryFn: () => api.get<PlaylistDetail>(`/playlists/${p.id}`),
      staleTime: 30_000,
    })),
  });
  const detail = new Map(details.flatMap((d) => (d.data ? [[d.data.id, d.data] as const] : [])));
  const rows = (playlists ?? []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
  const manage = can(me, 'playlists.manage');
  const pending = usePendingMap();

  return (
    <>
      <PageHead title={t('nav.playlists')} sub={t('playlists.sub')}
        actions={manage && <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('playlists.new')}</button>} />
      {isLoading ? <Spinner /> : !playlists?.length ? (
        <div className="card"><Empty icon="list" title={t('playlists.empty')} text={t('playlists.emptyText')}
          action={manage ? <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('playlists.new')}</button> : undefined} /></div>
      ) : (
        <>
          <div className="toolbar"><input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <div className="card scroll" style={{ padding: 10 }}>
            <table className="list">
              <thead><tr><th>{t('common.name')}</th><th>{t('playlists.items')}</th><th>{t('playlists.duration')}</th><th>{t('playlists.state')}</th><th>{t('playlists.usedBy')}</th><th>{t('playlists.changed')}</th></tr></thead>
              <tbody>
                {rows.map((p: Playlist) => {
                  const d = detail.get(p.id);
                  const total = d?.items.filter((i) => i.enabled).reduce((s, i) => s + (i.durationMs ?? p.defaultDurationMs), 0);
                  return (
                    <tr key={p.id} className="click" onClick={() => navigate(`/playlists/${p.id}`)}>
                      <td><b>{p.name}</b></td>
                      <td>{d ? d.items.length : '…'}</td>
                      <td>{total !== undefined ? formatDuration(total) : '…'}</td>
                      <td>{p.latestRevisionNo === null ? <span className="pill neutral">{t('playlists.draft')}</span>
                        : p.draftChanged ? <span className="pill warn">{t('playlists.unpublishedChanges')}</span>
                        : <span className="pill ok">{t('playlists.published')}</span>} <PendingPill request={pending.get(p.id)} /></td>
                      <td className="muted">{d ? t('playlists.usedByText', { a: d.usedBy.assignments, s: d.usedBy.schedules }) : '…'}</td>
                      <td className="muted">{relativeTime(p.updatedAt, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {create && <CreatePlaylistDialog onClose={() => setCreate(false)} onCreated={(id) => navigate(`/playlists/${id}`)} />}
    </>
  );
}

export function CreatePlaylistDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [secs, setSecs] = useState(10);
  const submit = async () => {
    try {
      const p = await api.post<Playlist>('/playlists', { name: name.trim(), defaultDurationMs: secs * 1000 });
      onCreated(p.id);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('playlists.new')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim()} onClick={submit}>{t('common.create')}</button>
    </>}>
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) void submit(); }} className="col">
        <Field label={t('common.name')}><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('playlists.namePh')} /></Field>
        <Field label={t('playlists.defaultDuration')}><input type="number" min={1} max={3600} value={secs} onChange={(e) => setSecs(Math.max(1, Number(e.target.value)))} /></Field>
      </form>
    </Dialog>
  );
}
